// @flow

import bowser from 'bowser';
// $FlowIgnoreMe WHY: because this package has no flow types
import parseContentDisposition from 'content-disposition-parser';

import {downloadFile} from '@pb/kit-util-download-file';

import {OBSOLETE_REQUEST_ERROR} from '../constants';
import {request} from '../request';
import {removeElement as remove} from './remove';
import {prepareRequestData} from '../prepare-request-data';

import type {DownloadFile, SuccessResponse, FailResponse} from '../typedef';

type GetFileName = (headerValue: ?string)=>string;
type GetContentDispositionValue = (headers: Headers)=>?string;

const SUCCESS_RESPONSE: SuccessResponse = {response: {ok: true}};
const FAIL_RESPONSE: FailResponse = {error: 'File loading failure'};
const CONTENT_DISPOSITION_LOWERCASE = 'content-disposition';
const CONTENT_DISPOSITION_CAPITALIZED = 'Content-Disposition';

export const getFileName: GetFileName = headerValue => {
    if (!headerValue) {
        return '';
    }
    const parsedContentDisposition = parseContentDisposition(headerValue);
    return parsedContentDisposition?.filename ?? '';
};

export const getContentDispositionValue: GetContentDispositionValue = headers => (
    headers.get(CONTENT_DISPOSITION_LOWERCASE) || headers.get(CONTENT_DISPOSITION_CAPITALIZED)
);

/*
* For downloading files by Iframe we cannot provide correct error handling in this situation.
* 1. We cannot take error from requests inside iframe without any huge amount of unnecessary checking errors, setTimeout's, assigning listeners etc.
* 2. In our project this request going always after requesting a file key from the server and then getting the file again from the server by this key.
* This is avoiding possibility to get some kind of error. If something goes wrong first request to a file will be broken, because it's use the same storage.
*
* Variant's to download the file and handling the error and why it's not good idea to use it:
*   1. In case if we use fetch we will get the double request's on each download, if we're taking to account the weight of the files, it's not a good idea to work with.
*   2. In case with rewriting this to link ('a href="Link to the file"'), in this case we cannot cover errors after we clicked, because it will try to download immediately.
* In this case we will see only the error by browser that it's tried to download something but didn't get anything, if we got the invalid key for example.
*   3. In case with fetch + link + Blob, this case was rejected because we don't know how big are file's can be, and in some scenarios we can lose the file data.
* */

export const downloadByIFrame: DownloadFile = (
    callInfo,
    API_ROOT
): Promise<typeof SUCCESS_RESPONSE | typeof FAIL_RESPONSE> => {
    const {url} = prepareRequestData(callInfo, API_ROOT);
    if (!url) {
        return Promise.resolve(FAIL_RESPONSE);
    }
    if (bowser.ios) {
        window.location.assign(url);
        return Promise.resolve(SUCCESS_RESPONSE);
    }

    const propsForHiding = {width: '0', height: '0'};

    let iframe = document.createElement('iframe');
    iframe = Object.assign(iframe, {src: url, ...propsForHiding});

    if (document.body) {
        document.body.appendChild(iframe);
        setTimeout(() => remove(iframe), 30000);
    }
    return Promise.resolve(SUCCESS_RESPONSE);
};

export const downloadByStream: DownloadFile = (callInfo, API_ROOT) => {
    return request(callInfo, API_ROOT)
        .then(({responseData, response}) => {
            const fileName = getFileName(getContentDispositionValue(response.headers));

            if (fileName) {
                downloadFile(responseData, fileName);
            }

            return Promise.resolve({responseData, response});
        })
        .catch(error => {
            if (error.name === 'AbortError') {
                return Promise.resolve({error: OBSOLETE_REQUEST_ERROR});
            }

            return Promise.resolve({error});
        });
};

            // @pb/generate-flow-typed Webpack Loader:
            // Duplicates all internal dependencies imports
            // to be sure all deps are parsed by this loader
            // cause Babel loader removes `import type ...`
            // and e.g. 'typedef.js' files are not included in dist
            
require("../typedef");
